import { NEWSLETTER_MESSAGES } from "@config/NewsletterFormConstants";
import fetchTMApi from "@helper/fetchTMApi";
import {
  type PostConsentV3Item,
  type RespPostConsentV3,
  tryCatch,
} from "@sphtech/sph-web-ui";
import type { NewsletterPostResponse } from "@typings/Newsletter";

/**
 * Result of a newsletter submission. Each field, when defined, is the message
 * to write into the corresponding alert slot in the form. `undefined` means
 * "leave that slot unchanged".
 */
export type NewsletterSubmitResult = {
  errorMsg?: string;
  successMsg?: string;
};

export type NewsletterSubmitProps = {
  email: string;
  isMarketingConsent?: boolean;
  isUnsubscribe?: boolean;
};

/**
 * Submit the newsletter signup via the Consent v3 API
 * (`/_plat/api/v1/consent/sync`).
 */
export async function submitViaConsentV3({
  email,
  isMarketingConsent = false,
  isUnsubscribe = false,
}: NewsletterSubmitProps): Promise<NewsletterSubmitResult> {
  const endpoint = "/_plat/api/v1/consent/sync";

  const consents: PostConsentV3Item[] = [
    {
      name: "tm_newsletter",
      status: isUnsubscribe ? "withdrawn" : "confirmed",
    },
  ];
  // Only send marketing consent when it is opt-in
  if (isMarketingConsent) {
    consents.push({
      name: "sph_marketing_email",
      status: "confirmed",
    });
  }
  const payload: { identifier: string; consents: PostConsentV3Item[] } = {
    identifier: email,
    consents,
  };
  const config: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
  const request = fetch(endpoint, config);
  const { error, response } = await tryCatch(request);

  if (error) {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }

  // Right now we are only cast, we dont really check if json has the correct shape.
  // Anyway the endpoint is internal, so it is mostly expected to return this type.
  // But it would still be recommended to check shape of the returned value instead.
  const json = (await response.json()) as RespPostConsentV3;

  if (!json.success) {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }

  return {
    successMsg: isUnsubscribe
      ? NEWSLETTER_MESSAGES.UNSUB_SUCCESS
      : NEWSLETTER_MESSAGES.SUCCESS,
  };
}

/**
 * Submit the newsletter signup via the legacy `newsletterSignup` endpoint.
 * Used while the Consent v3 feature flag is disabled.
 */
export async function submitViaLegacyEndpoint({
  email,
  isMarketingConsent = false,
}: NewsletterSubmitProps): Promise<NewsletterSubmitResult> {
  try {
    const queryParams = new URLSearchParams({
      email,
      isMarketingChecked: String(isMarketingConsent),
    }).toString();
    const res = await fetchTMApi(`newsletterSignup?${queryParams}`, "GET");
    const data = res as NewsletterPostResponse;
    if (data.msg) {
      return { errorMsg: "", successMsg: data.msg };
    }
    if (data.errorMsg) {
      return { errorMsg: data.errorMsg };
    }
    return {};
  } catch {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }
}

export async function unsubscribeViaLegacyEndpoint({
  email,
}: NewsletterSubmitProps): Promise<NewsletterSubmitResult> {
  try {
    const res = await fetchTMApi("newsletterUnSubscribe", "POST", {
      email,
    });
    const data = res as NewsletterPostResponse;
    if (data.msg) {
      return { errorMsg: "", successMsg: data.msg };
    }
    if (data.errorMsg) {
      return { errorMsg: data.errorMsg };
    }
    return {};
  } catch {
    return { errorMsg: NEWSLETTER_MESSAGES.FAILED };
  }
}
